import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { X, ChevronRight, ChevronDown } from 'lucide-react';

/**
 * ScoringGuideModal - Modal displaying scoring guide and scale information
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Handler to close the modal
 */
const ScoringGuideModal = ({ isOpen, onClose }) => {
  const [isScoringGuideCollapsed, setIsScoringGuideCollapsed] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Scoring Guide</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close scoring guide"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Collapsible Scoring Image */}
          <div className="mb-4">
            <button
              onClick={() => setIsScoringGuideCollapsed(!isScoringGuideCollapsed)}
              className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
            >
              <span className="font-semibold text-gray-900">Scoring Reference Chart</span>
              {isScoringGuideCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
            </button>

            {!isScoringGuideCollapsed && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <img
                  src="/scoring-guide.png"
                  alt="Scoring Guide"
                  className="w-full h-auto rounded"
                />
              </div>
            )}
          </div>

          {/* Scoring Descriptions */}
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">How Scores Are Calculated</h4>
              <p className="text-sm text-gray-600">
                Each question is scored on a scale that reflects the strength of that aspect of your business.
                The overall score is calculated by averaging all category scores, giving you a comprehensive
                view of your business readiness and attractiveness for maximum value.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Score Ranges</h4>
              <p className="text-sm text-gray-600 mb-3">Based on the assessment scoring scale where average performance is around 50%:</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#c49e73' }}></div>
                  <span className="text-sm"><strong>86-100% (No Gaps):</strong> Exceptional performance, best in class</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#a7d5a8' }}></div>
                  <span className="text-sm"><strong>72-86% (Minor Gaps):</strong> Strong performance, minor improvements possible</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#b8d4e8' }}></div>
                  <span className="text-sm"><strong>57-72% (Considerable Gaps):</strong> Average to good, clear room for improvement</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f4ebb0' }}></div>
                  <span className="text-sm"><strong>43-57% (Critical Gaps):</strong> Below average, significant work needed</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f5d7b3' }}></div>
                  <span className="text-sm"><strong>28-43% (Very Critical Gaps):</strong> Poor performance, urgent attention required</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f5c9c9' }}></div>
                  <span className="text-sm"><strong>0-28% (Extremely Critical):</strong> Critical deficiency or not applicable</span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> A score of 67% is the "green zone" target where businesses become much more attractive to buyers. A score of 72% or above is considered "best in class," representing clear, documented, and transferable processes.
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Using Your Results</h4>
              <p className="text-sm text-gray-600 mb-2">
                Focus on categories with lower scores first. Click on any category to expand and see
                individual questions. Use the gap distribution chart to filter questions by score range
                and prioritize your improvement efforts.
              </p>
              <p className="text-sm text-gray-600">
                Remember: A lower score isn't bad—it's an opportunity. It shows you exactly where to focus
                your efforts to maximize your exit value.
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition min-h-[44px]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

ScoringGuideModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default ScoringGuideModal;
